import { ReactElement, useEffect, useMemo, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import Paper from '@material-ui/core/Paper';

import { IHospitalEventList, IHospitalListFilters } from '~/services/api/hospital/types';
import { AdtEventAction } from '~/services/api/enums';
import { useStoreActions, useStoreState } from '~/store/hooks';
import { extractErrorMessage } from '~/utils/error';
import { formatToIsoWithoutTimezone } from '~/utils/date';
import { ISort } from '~/utils/sort/types';
import Loader from '~/ui/components/common/Loader';
import Header from './Header/Header';
import Table from './Table/Table';
import HospitalCounters from './Counters';
import PatientsInFacility from './PatientsInFacility';
import DetailsModal from './DetailsModal';
import HistoryModal from './HistoryModal';

import styles from './Hospital.module.scss';
import getInitialValues from './Header/form/getInitialValues';
import { IHistoryModalState } from './types';

const Hospital = (): ReactElement => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSelectorsLoading, setIsSelectorsLoading] = useState(true);
  const [filters, setFilters] = useState<IHospitalListFilters>(getInitialValues());
  const [sort, setSort] = useState<ISort>({
    orderBy: 'messageTimestamp',
    isDescending: true,
  });
  const [eventIdsPreset, setEventIdsPreset] = useState<AdtEventAction[]>(null);
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);
  const [historyModalState, setHistoryModalState] = useState<IHistoryModalState | null>(null);

  const { events, details, pagination, selectors, counters } = useStoreState(
    state => state.hospital,
  );
  const {
    onGetHospitalEvents,
    onGetHospitalCounters,
    onGetMoreHospitalEvents,
    onGetHospitalSelectors,
    onGetHospitalDetails,
    clearHospitalDetails,
  } = useStoreActions(actions => actions.hospital);
  const showError = useStoreActions(actions => actions.snackbar.showError);

  const requestPayload = useMemo(
    () => ({
      ...filters,
      ...sort,
      startDate: formatToIsoWithoutTimezone(filters.startDate),
      endDate: formatToIsoWithoutTimezone(filters.endDate),
    }),
    [filters, sort],
  );

  const getSelectors = async () => {
    setIsSelectorsLoading(true);

    try {
      await onGetHospitalSelectors();
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setIsSelectorsLoading(false);
    }
  };

  const getData = async () => {
    setIsLoading(true);

    try {
      await Promise.all([
        onGetHospitalEvents(requestPayload),
        onGetHospitalCounters(requestPayload),
      ]);
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
  };

  const onLoadMore = async () => {
    try {
      await onGetMoreHospitalEvents(requestPayload);
    } catch (e) {
      showError(extractErrorMessage(e));
    }
  };

  const updateEventFilter = (eventIds: AdtEventAction[]) => {
    const nextEventIds = [...eventIds] as AdtEventAction[];
    setEventIdsPreset(nextEventIds);
    setFilters(prev => ({ ...prev, eventIds: nextEventIds }));
  };

  const onFiltersSubmit = (payload: IHospitalListFilters) => {
    setEventIdsPreset(null);
    setFilters(payload);
  };

  const onOpenDetails = async (event: IHospitalEventList) => {
    if (isDetailsLoading) return;

    clearHospitalDetails();
    setIsDetailsLoading(true);

    try {
      await onGetHospitalDetails(event.id);
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setIsDetailsLoading(false);
    }
  };

  const onCloseDetailsModal = () => {
    clearHospitalDetails();
  };

  const onOpenHistoryModal = (patientId: number, patientFullName: string) => {
    setHistoryModalState({
      patientId,
      patientFullName,
    });
  };

  const onCloseHistoryModal = () => {
    setHistoryModalState(null);
  };

  useEffect(() => {
    getData();
  }, [requestPayload]);

  useEffect(() => {
    getSelectors();
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.top}>
        <div className={styles.title}>Hospital</div>
        <PatientsInFacility count={counters?.patientsInFacilityCount ?? 0} />
      </div>
      <HospitalCounters counters={counters} onClick={updateEventFilter} />
      <Paper elevation={3} className={styles.tableContainer}>
        <Header
          selectors={selectors}
          isLoading={isSelectorsLoading}
          eventIdsPreset={eventIdsPreset}
          onSubmit={onFiltersSubmit}
        />
        <InfiniteScroll
          dataLength={events.length}
          next={onLoadMore}
          hasMore={pagination.hasMore}
          loader={<Loader />}
        >
          {!isLoading && (
            <Table events={events} sort={sort} onSort={setSort} onOpenDetails={onOpenDetails} />
          )}
        </InfiniteScroll>
        {isLoading && <Loader />}
      </Paper>
      {details && historyModalState === null && (
        <DetailsModal
          details={details}
          onClose={onCloseDetailsModal}
          onViewHistory={onOpenHistoryModal}
        />
      )}
      {historyModalState !== null && (
        <HistoryModal
          patientId={historyModalState.patientId}
          patientFullName={historyModalState.patientFullName}
          onClose={onCloseHistoryModal}
        />
      )}
    </div>
  );
};

export default Hospital;
